import { Basket } from '@df/session/api/basket/basket';
import { BasketItem, IBasketItemData } from '@df/session/api/basket/basket-item';
import { Customer } from '@df/session/api/customer';
import compact from 'lodash-es/compact';
import { GiftboxService } from './giftbox.service';

export interface IGiftboxBasketItemData extends IBasketItemData {
    rowPrice?: number;
    tax?: number;
    added_at?: string;
}

export class GiftboxBasketItem extends BasketItem {
    protected giftboxService: GiftboxService;

    /**
     * Basket items kept in this box (duplicates allowed for pieces)
     * @type {number[]}
     */
    protected _items: number[] = [];

    /**
     * This is to help us with checkbox for gift message
     * We want to be able to switch on off message but BE is message field
     * @type {boolean}
     */
    protected _hasMessage: boolean = false;

    /**
     * When switching message on/off we buffer last one here
     * not to loose the value
     */
    protected _lastMessage?: string;

    constructor(
        data: IGiftboxBasketItemData,
        basket: Basket,
        public customer: Customer,
        protected index: number,
        giftboxService: GiftboxService
    ) {
        super(data, basket);
        this.giftboxService = giftboxService;

        const contentOption = giftboxService.getContentsOption();
        if (contentOption) {
            const option = this.getCustomOption(contentOption.id);
            this._items = option ? (option + '').split(',').map(i => parseInt(i, 10)) : [];
        }

        // show message fields if any related data is set
        if (this.message) {
            this.hasMessage = true;
        }
    }

    public get boxLabel(): string {
        if (!this.id) {
            return 'No gift box';
        }
        return 'Box ' + this.index;
    }

    /**
     * Message getter
     */
    public get message(): string | undefined {
        const option = this.giftboxService.getMessageOption();
        if (option) {
            return this.getCustomOption(option.id);
        }
        return undefined;
    }

    /**
     * Sets gift message option
     * @param {string} message [description]
     */
    public set message(message: string | undefined) {
        const option = this.giftboxService.getMessageOption();

        if (!option) {
            console.error('Could not identify message option');
        } else {
            this.setCustomOption(option.id, message);
        }
    }

    /**
     * Sets gift message option
     * @param {string} message [description]
     */
    public set name(name: string | undefined) {
        const option = this.giftboxService.getNameOption();

        if (!option) {
            console.error('Could not identify message option');
        } else {
            this.setCustomOption(option.id, name);
        }
    }

    /**
     * Message getter
     */
    public get name(): string | undefined {
        const option = this.giftboxService.getNameOption();
        if (option) {
            return this.getCustomOption(option.id);
        }
        return undefined;
    }

    /**
     * has message setter
     * @param {boolean} value [description]
     */
    public set hasMessage(value: boolean) {
        if (value) {
            if (this._lastMessage) {
                this.message = this._lastMessage;
            }
        } else {
            const msg = this.message;
            if (msg) {
                this._lastMessage = msg;
            }
            this.message = undefined;
        }
        this._hasMessage = value;
    }

    /**
     * _hasMessage getter
     * @return {boolean}
     */
    public get hasMessage(): boolean {
        return this._hasMessage;
    }

    /**
     * Items ids in box getter
     * returns array of item ids in box item
     */
    public get contents(): number[] {
        return this._items;
    }

    /**
     *  Basket items in box getter
     *  returns array of basket items in box item
     */
    public get basketItems(): BasketItem[] {
        return <BasketItem[]>compact(this._items.map(id => this.customer['basket'].byId(id)));
    }

    /**
     * Retrieves number of items in box
     */
    public get contentsQty() {
        return this._items.length;
    }

    /**
     * Removes item (one piece) from the box
     * @param {number} itemId Basket item id
     */
    public popItem(itemId: number): void {
        this._items.splice(this._items.indexOf(itemId), 1);
        this.onItemsChange();
    }

    /**
     * Puts item (one piece) in box
     * @param {number} itemId Basket item it
     */
    public pushItem(itemId: number): void {
        this._items.push(itemId);
        this.onItemsChange();
    }

    /**
     * Updates basketItem custom options
     * when this._items change (item is added/removed from box)
     */
    protected onItemsChange() {
        const contentOption = this.giftboxService.getContentsOption();
        if (!contentOption) {
            console.error('Could not identify content option');
        } else {
            this.setCustomOption(contentOption.id, this._items.join(','));
        }
    }
}
export interface IBoxBasketItemFactory {
    create: (index: number, data: IBasketItemData, basket: Basket) => GiftboxBasketItem;
}

export function createGiftboxBasketItem(
    giftboxService: GiftboxService,
    customer: Customer,
    index: number,
    data: IBasketItemData,
    basket: Basket
) {
    return new GiftboxBasketItem(data, basket, customer, index, giftboxService);
}
